import React, { useState, useRef, useEffect, useCallback, useTransition, useDeferredValue } from "react";
import { Link } from "react-router-dom";

import { motion } from "framer-motion";
import {
  MapPin,
  Phone,
  Mail,
  Clock,
  Briefcase,
  HelpCircle,
  ArrowUpRight,
  SendHorizontal,
  PhoneForwarded,
  ChevronDown,
  X,
} from "lucide-react";
import { Grid } from "../../ui/Grid";
import {
  upAndDown,
  downAndUp,
  scrollVariantsLeft,
  scrollVariantsRight,
} from "../../../constants/TextAnimation";
import helpers from "../../../lib/crypto";
import Joi from "joi";
import axios from "axios";
import confetti from "canvas-confetti";
import { useFunctionContext } from "../../context/FunctionalContext";
import countryList from "../../../utils/data";

const API_URL = import.meta.env.VITE_API_URL;

const services = [
  "Business Advisory",
  "Finance and Accounting",
  "Real Estate Services",
  "Other",
];

const referredBy = [
  "Wahaj",
  "Rabia",
  "Lysann",
  "Maricel",
  "Bhuvan",
  "Jennifer",
  "Nikola",
  "Other",
];

const Dropdown = React.memo(({ label, options, value, setValue }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex justify-between items-center px-4 py-3 focus:ring-1 focus:ring-gold placeholder-gray-500 transition-colors duration-200 bg-slate-800/60 border border-gold/10 rounded-md focus:outline-none focus:border-gold caret-gold"
      >
        <span className={value ? "text-white" : "text-gray-500"}>
          {value || `Select ${label}`}
        </span>
        <span className="text-gray-400">▾</span>
      </button>

      {open && (
        <ul
          className="absolute z-20 mt-2 max-h-52 overflow-y-auto w-full rounded-xl bg-slate-800 shadow-lg border border-gray-600 overflow-hidden"
        >
          {options.map((option) => (
            <li
              key={option}
              onClick={() => {
                setValue(option);
                setOpen(false);
              }}
              className="px-4 py-3 hover:bg-slate-700 cursor-pointer text-sm"
            >
              {option}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
});

Dropdown.displayName = "Dropdown";

const Contact = () => {
  const { ORG_ID } = useFunctionContext();

  const [loading, setLoading] = useState(false);
  const [selectedCode, setSelectedCode] = useState("+91");
  const [selectedEmoji, setSelectedEmoji] = useState("🇮🇳");
  const [phoneNumberModalOpen, setPhoneNumberModalOpen] = useState(false);
  const searchInputRef = useRef(null);
  const [search, setSearch] = useState("");
  const deferredSearch = useDeferredValue(search);
  const [isPending, startTransition] = useTransition();
  const [visibleCount, setVisibleCount] = useState(30);

  const [formData, setFormData] = useState({
    email: "",
    name: "",
    phoneNumber: "",
    services: "",
    referredBy: "",
    message: "",
  });

  const contactRef = useRef(null);
  const [errors, setErrors] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const handleInputChange = useCallback(({ currentTarget: inp }) => {
    const { name, value } = inp;

    // Immediately update for responsive typing
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Defer the expensive validation
    startTransition(() => {
      let newValue = value;

      if (name === "message") {
        newValue = value.replace(/[^\w\s.\-/:?,]/gi, "");
      }

      if (name === "name") {
        newValue = value.replace(/[^\w\s]/gi, "");
      }

      if (name === "email") {
        newValue = value.replace(/[^a-zA-Z0-9@,. ]/gi, "");
      }

      // Only update if sanitization changed the value
      if (newValue !== value) {
        setFormData((prev) => ({
          ...prev,
          [name]: newValue,
        }));
      }
    });
  }, []);

  const handlePhoneChange = ({ currentTarget: inp }) => {
    const { value } = inp;
    const newValue = value.replace(/[^0-9]/g, "");
    setFormData((prev) => ({ ...prev, phoneNumber: newValue }));
  };

  const handleServicesChange = useCallback((value) => {
    setFormData((prev) => ({ ...prev, services: value }));
  }, []);

  const handleReferredByChange = useCallback((value) => {
    setFormData((prev) => ({ ...prev, referredBy: value }));
  }, []);

  const scrollToContact = () => {
    contactRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const add_leads = (data) => {
    const schema = Joi.object({
      key: Joi.string().required(),
      source: Joi.string().required(),
      lead_name: Joi.string().trim().min(2).max(30).required(),
      email: Joi.string()
        .trim()
        .min(10)
        .max(55)
        .email({ tlds: { allow: false } })
        .required()
        .messages({
          "string.email": "Please enter a valid email address",
        }),
      phoneNumber: Joi.string().min(6).max(15).required(),
      services: Joi.string().min(3).max(100).required(),
      referredBy: Joi.string().min(3).max(100).required(),
      admin_type: Joi.string().optional().allow("", null),
      organisation_id: Joi.string().required(),
      comments: Joi.string().optional().allow("", null),
      route_action: Joi.number().valid(1).required(),
      status: Joi.string().trim().valid("New").required(),
      next_follow_up: Joi.date().iso().allow("", null),
    });

    return schema.validate(data);
  };

  const dosubmit = async (e) => {
    e.preventDefault();
    console.log("form submitted");
    setLoading(true);

    if (!formData?.email || !formData?.name || !formData?.message || !formData?.phoneNumber || !formData?.services || !formData?.referredBy) {
      setErrors("Please fill all the fields");
      setLoading(false);
      return;
    }

    const payload = {
      key: "scanglobal",
      source: "Advisory",
      lead_name: formData.name,
      email: formData.email,
      comments: formData.message || "",
      phoneNumber: selectedCode.replace("+", "") + formData.phoneNumber,
      services: formData.services,
      referredBy: formData.referredBy,
      route_action: 1,
      status: "New",
      next_follow_up: new Date().toISOString(),
      admin_type: "1",
      organisation_id: ORG_ID,
    };

    const { error } = add_leads(payload);

    if (error) {
      // console.log("error is present")
      setErrors(error.details[0].message);
      setLoading(false);
      return;
    }

    let encrypted;
    try {
      encrypted = helpers.encryptobj(payload);
      // console.log("📦 Encrypted Payload to Send:", encrypted);
    } catch (e) {
      // console.error("❌ Encryption error:", e);
      setErrors("Encryption failed.");
      setLoading(false);
      return;
    }

    try {
      console.log("🚀 Initiating Axios POST request...");
      const response = await axios.post(
        `${API_URL}/admin/add_update_lead`,
        { enc: encrypted },
        { headers: { "Content-Type": "application/json" } }
      );

      // console.log("✅ Axios Response:", response.data);

      if (response.status === 200) {
        setSuccessMessage("Your message has been sent successfully!");
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 },
        });
        setErrors("");
        setFormData({ name: "", email: "", phoneNumber: "", services: "", referredBy: "", message: "" });
      } else {
        setErrors(
          response.data?.message || "Something went wrong on the server"
        );
      }
    } catch (err) {
      console.error("❌ Axios Submit Error:", err);
      if (err.response) {
        console.error("💡 Server Response:", err.response.data);
      }
      setErrors("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Detect country based on IP
    const detectCountry = async () => {
      try {
        const response = await fetch('https://ipapi.co/json/');
        const data = await response.json();

        if (data.country_code) {
          const country = countryList.find(c => c.code === data.country_code);
          if (country) {
            setSelectedCode(country.dial_code);
            setSelectedEmoji(country.emoji);
            console.log(`🌍 Auto-detected country via IP: ${country.name} (${country.dial_code})`);
            return;
          }
        }
      } catch (error) {
        console.log('IP detection failed, trying browser geolocation...');
      }

      // Fallback to browser geolocation
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            try {
              const { latitude, longitude } = position.coords;
              const response = await fetch(
                `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
              );
              const data = await response.json();

              if (data.address && data.address.country_code) {
                const countryCode = data.address.country_code.toUpperCase();
                const country = countryList.find(c => c.code === countryCode);
                if (country) {
                  setSelectedCode(country.dial_code);
                  setSelectedEmoji(country.emoji);
                  console.log(`📍 Auto-detected country via GPS: ${country.name} (${country.dial_code})`);
                }
              }
            } catch (error) {
              console.log('Geolocation reverse geocoding failed, using default');
            }
          },
          (error) => {
            console.log('Geolocation permission denied or unavailable, using default');
          }
        );
      }
    };

    detectCountry();
  }, []);

  const filteredCountries = React.useMemo(() => {
    return countryList.filter(
      (c) =>
        c.name.toLowerCase().includes(deferredSearch.toLowerCase()) ||
        c.dial_code.includes(deferredSearch)
    );
  }, [deferredSearch]);

  useEffect(() => {
    setVisibleCount(30);
  }, [deferredSearch]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(""), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  return (
    <>
      {/* <section className="py-10 sm:py-16 lg:py-24">
        <div className="container px-4 py-10 sm:px-6 lg:px-8 lg:py-14 mx-auto">
          <div className="max-w-2xl lg:max-w-5xl mx-auto">
            <div className="text-center">
              <h1 className="text-3xl font-bold md:text-4xl text-white">
                Contact us
              </h1>
              <p className="mt-1 text-neutral-400">
                We'd love to talk about how we can help you.
              </p>
            </div>

            <div className="mt-12 grid items-center lg:grid-cols-2 gap-6 lg:gap-16">
              <div className="flex flex-col border rounded-xl p-4 sm:p-6 lg:p-8 border-neutral-700">
                <h2 className="mb-8 text-xl font-semibold text-neutral-200">
                  Fill in the form
                </h2>

                <form>
                  <div className="grid gap-4">
                
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label
                          htmlFor="hs-firstname-contacts-1"
                          className="sr-only"
                        >
                          First Name
                        </label>
                        <input
                          type="text"
                          name="hs-firstname-contacts-1"
                          id="hs-firstname-contacts-1"
                          className="py-2.5 sm:py-3 px-4 block w-full rounded-lg sm:text-sm focus:border-gold  disabled:opacity-50 disabled:pointer-events-none bg-neutral-900 border-neutral-700 text-neutral-400 placeholder-neutral-500 focus:ring-gold focus:outline-none focus:ring-1 "
                          placeholder="First Name"
                        />
                      </div>
                    </div>
                

                    <div>
                      <label htmlFor="hs-email-contacts-1" className="sr-only">
                        Email
                      </label>
                      <input
                        type="email"
                        name="hs-email-contacts-1"
                        id="hs-email-contacts-1"
                        autoComplete="email"
                        className="py-2.5 sm:py-3 px-4 block w-full rounded-lg sm:text-sm focus:border-gold  disabled:opacity-50 disabled:pointer-events-none bg-neutral-900 border-neutral-700 text-neutral-400 placeholder-neutral-500 focus:ring-gold focus:outline-none focus:ring-1 "
                        placeholder="Email"
                      />
                    </div>

                    <div>
                      <label htmlFor="hs-phone-number-1" className="sr-only">
                        Phone Number
                      </label>
                      <input
                        type="text"
                        name="hs-phone-number-1"
                        id="hs-phone-number-1"
                        className="py-2.5 sm:py-3 px-4 block w-full rounded-lg sm:text-sm focus:border-gold  disabled:opacity-50 disabled:pointer-events-none bg-neutral-900 border-neutral-700 text-neutral-400 placeholder-neutral-500 focus:ring-gold focus:outline-none focus:ring-1 "
                        placeholder="Phone Number"
                      />
                    </div>

                    <div>
                      <label htmlFor="hs-about-contacts-1" className="sr-only">
                        Details
                      </label>
                      <textarea
                        id="hs-about-contacts-1"
                        name="hs-about-contacts-1"
                        rows="4"
                        className="py-2.5 sm:py-3 px-4 block w-full rounded-lg sm:text-sm focus:border-gold  disabled:opacity-50 disabled:pointer-events-none bg-neutral-900 border-neutral-700 text-neutral-400 placeholder-neutral-500 focus:ring-gold focus:outline-none focus:ring-1 "
                        placeholder="Details"
                      ></textarea>
                    </div>
                  </div>
              

                  <div className="mt-4 grid">
                    <button
                      type="submit"
                      className="w-full py-3 px-4 inline-flex justify-center items-center gap-x-2 text-sm font-medium rounded-lg border border-transparent bg-gold text-slate-900 hover:bg-gold focus:outline-hidden focus:bg-gold disabled:opacity-50 disabled:pointer-events-none hover:opacity-85 transition duration-200"
                    >
                      Submit
                    </button>
                  </div>

                  <div className="mt-3 text-center">
                    <p className="text-sm text-neutral-500">
                      We'll get back to you in 1-2 business days.
                    </p>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section> */}

      {/* hero */}
      <section className="py-8 md:py-16 lg:pb-24 lg:pt-20">
        <div className="relative pt-16 pb-32 flex content-center items-center justify-center">
          <div
            className="absolute top-0 w-full h-full bg-center bg-cover bg-fixed"
            style={{
              backgroundImage: `url("/images/solutions/10.webp")`,
            }}
          >
            <span className="w-full h-full absolute opacity-60 bg-black"></span>
          </div>

          <div className="container relative mx-auto">
            <motion.div
              initial="hidden"
              whileInView="visible"
              variants={downAndUp}
              viewport={{ amount: 0.3 }}
              className="items-center flex flex-wrap"
            >
              <div className="w-full lg:w-6/12 px-4 ml-auto mr-auto text-center mt-24">
                <div className="md:pr-12">
                  <h1 className="text-white font-bold text-4xl md:text-5xl leading-tight">
                    Get in Touch
                  </h1>
                  <p className="mt-4 text-lg text-gray-300">
                    We&apos;re here to help with all your technology needs
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      <section
        ref={contactRef}
        className="pb-12 px-4 md:px-8 relative z-10 container"
      >
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={upAndDown}
          viewport={{ amount: 0.3 }}
          className="grid grid-cols-1 gap-6 px-8 md:px-0 md:grid-cols-2"
        >
          <div className="flex items-center justify-center">
            {/* Left line */}
            <div className="block h-px w-16 md:w-24 bg-gradient-to-r from-transparent via-gold to-transparent mr-4"></div>

            {/* Text */}
            <h4
              className="text-xl md:text-4xl font-semibold text-center uppercase"
            // style={{ fontFamily: "Lato", fontWeight: "900" }}
            >
              Contact Us
            </h4>

            {/* Right line */}
            <div className="block h-px w-16 md:w-24 bg-gradient-to-l from-transparent via-gold to-transparent ml-4"></div>
          </div>
          <p className="text-base leading-relaxed text-gray-400">
            If you have any questions, please feel free to get in touch with us
            via phone, text, email the form below, or even social media!
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-10">
          <motion.div
            variants={scrollVariantsLeft}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.5 }}
            className="bg-slate-900/40 rounded-xl py-6 px-6"
          >
            <div className="flex items-center gap-3">
              <div className="w-1 h-6 bg-gradient-to-b from-secondary to-gold rounded-sm" />
              <h3 className="text-lg leading-none font-semibold uppercase text-center">
                Get in touch
              </h3>
            </div>
            <div className="my-6 h-px bg-gradient-to-r from-gray-400 via-gray to-gray-400 animate-pulse" />

            <form action="#" method="POST" className="" onSubmit={dosubmit}>
              <div className="grid grid-cols-1 gap-x-5 gap-y-4">
                <div className="flex gap-5">
                  <div className="flex-auto">
                    <label
                      htmlFor=""
                      className="text-base font-medium text-gray-400"
                    >
                      {" "}
                      Your name{" "}
                    </label>
                    <div className="mt-2.5 relative">
                      <input
                        type="text"
                        name="name"
                        value={formData?.name}
                        onChange={handleInputChange}
                        id=""
                        placeholder="Enter your full name"
                        className="block w-full px-4 py-3 focus:ring-1 focus:ring-gold placeholder-gray-500 transition-all duration-200 bg-slate-800/60 border border-gold/10 rounded-md focus:outline-none focus:border-gold caret-gold"
                      />
                    </div>
                  </div>

                  <div className="flex-auto">
                    <label
                      htmlFor=""
                      className="text-base font-medium text-gray-400"
                    >
                      {" "}
                      Email address{" "}
                    </label>
                    <div className="mt-2.5 relative">
                      <input
                        type="email"
                        name="email"
                        value={formData?.email}
                        onChange={handleInputChange}
                        id=""
                        placeholder="Enter your Email "
                        className="block w-full px-4 py-3 focus:ring-1 focus:ring-gold placeholder-gray-500 transition-all duration-200 bg-slate-800/60 border border-gold/10 rounded-md focus:outline-none focus:border-gold caret-gold"
                      />
                    </div>
                  </div>
                </div>

                {/* Phone Number */}
                <div>
                  <label className="text-base font-medium text-gray-400">
                    Phone Number
                  </label>
                  <div className="mt-2.5 relative">
                    <div className="flex items-center w-full px-3 py-3 bg-slate-800/60 border border-gold/10 rounded-md focus-within:border-gold transition">
                      {/* Country selector */}
                      <div className="relative">
                        <button
                          type="button"
                          className="flex items-center gap-1 text-sm text-gray-300 pr-2"
                          onClick={() => {
                            setPhoneNumberModalOpen(!phoneNumberModalOpen);
                            setVisibleCount(30);
                          }}
                        >
                          <span>{selectedEmoji}</span>
                          <span>{selectedCode}</span>
                          <ChevronDown className="size-3" />
                          <span className="mx-1 text-gray-500">|</span>
                        </button>

                        {phoneNumberModalOpen && (
                          <div
                            className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/60 backdrop-blur-sm transition-opacity duration-300"
                            onClick={() => setPhoneNumberModalOpen(false)}
                          >
                            <div
                              className="w-full max-w-md bg-slate-900 border border-slate-700 rounded-xl shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div className="flex items-center justify-between p-4 border-b border-slate-700 bg-slate-900">
                                <h3 className="text-white font-semibold text-lg">
                                  Select Country
                                </h3>
                                <button
                                  type="button"
                                  onClick={() => setPhoneNumberModalOpen(false)}
                                  className="text-gray-400 hover:text-white transition-colors p-1 hover:bg-slate-800 rounded-full"
                                >
                                  <X className="size-6" />
                                </button>
                              </div>
                              <div className="p-3 bg-slate-900 border-b border-slate-700">
                                <input
                                  ref={searchInputRef}
                                  autoFocus
                                  type="text"
                                  placeholder="Search country..."
                                  defaultValue=""
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    startTransition(() => {
                                      setSearch(value);
                                    });
                                  }}
                                  className="w-full px-4 py-2.5 text-base bg-slate-800 border border-slate-600 rounded-lg focus:outline-none focus:border-gold text-white placeholder-gray-500"
                                />
                              </div>

                              <div
                                className="max-h-[50vh] overflow-y-auto"
                                onScroll={(e) => {
                                  const { scrollTop, scrollHeight, clientHeight } = e.target;
                                  if (scrollHeight - scrollTop <= clientHeight * 1.5) {
                                    setVisibleCount(prev => Math.min(prev + 20, filteredCountries.length));
                                  }
                                }}
                              >
                                {filteredCountries
                                  .slice(0, visibleCount)
                                  .map((c) => (
                                    <div
                                      key={c.code}
                                      className="px-4 py-3 flex justify-between items-center hover:bg-slate-800 cursor-pointer border-b border-slate-800/50 last:border-0 transition-colors"
                                      onClick={() => {
                                        setSelectedCode(c.dial_code);
                                        setSelectedEmoji(c.emoji);
                                        setPhoneNumberModalOpen(false);
                                        setSearch("");
                                      }}
                                    >
                                      <span className="flex items-center gap-3 text-base">
                                        <span className="text-2xl">{c.emoji}</span>{" "}
                                        <span className="text-gray-200 font-medium">
                                          {c.name}
                                        </span>
                                      </span>
                                      <span className="text-gray-400 font-medium bg-slate-800 px-2 py-1 rounded text-sm">
                                        {c.dial_code}
                                      </span>
                                    </div>
                                  ))}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Phone input */}
                      <input
                        type="tel"
                        inputMode="numeric"
                        placeholder="Enter contact number"
                        value={formData.phoneNumber}
                        onChange={handlePhoneChange}
                        className="flex-1 bg-transparent text-sm focus:outline-none caret-gold"
                      />
                    </div>
                  </div>
                </div>

                {/* Services */}
                <div>
                  <label className="text-base font-medium text-gray-400">
                    Services
                  </label>
                  <div className="mt-2.5 relative">
                    <Dropdown
                      label="Services"
                      options={services}
                      value={formData.services}
                      setValue={handleServicesChange}
                    />
                  </div>
                </div>

                {/* Referred By */}
                <div>
                  <label className="text-base font-medium text-gray-400">
                    Referred By
                  </label>
                  <div className="mt-2.5 relative">
                    <Dropdown
                      label="Referred By"
                      options={referredBy}
                      value={formData.referredBy}
                      setValue={handleReferredByChange}
                    />
                  </div>
                </div>

                <div className="">
                  <label
                    htmlFor=""
                    className="text-base font-medium text-gray-400"
                  >
                    {" "}
                    Message{" "}
                  </label>
                  <div className="mt-2.5 relative">
                    <textarea
                      name="message"
                      onChange={handleInputChange}
                      id=""
                      placeholder=""
                      className="block w-full px-4 py-3 focus:ring-1 focus:ring-gold placeholder-gray-500 transition-all duration-200 bg-slate-800/60 border border-gold/10 rounded-md focus:outline-none focus:border-gold caret-gold"
                      rows="4"
                      value={formData?.message}
                    ></textarea>
                  </div>
                </div>

                <div className="text-cente">
                  <button
                    type="submit"
                    disabled={loading}
                    className={`px-16 py-2 mt-2 text-base flex items-center gap-2 font-semibold transition-all duration-200 bg-gradient rounded-md focus:outline-none uppercase ${loading ? "opacity-50 cursor-not-allowed" : ""
                      }`}
                  >
                    {loading ? "Sending..." : "Send message"}
                    {loading ? (
                      <span className="h-4 w-4 border-2 border-slate-900 border-t-transparent rounded-full animate-spin"></span>
                    ) : (
                      <SendHorizontal size={20} className="text-slate-900" />
                    )}
                  </button>

                  {/* Feedback messages */}
                  {errors && (
                    <p className="mt-4 text-sm text-red-400 font-medium text-start">
                      {errors}
                    </p>
                  )}

                  {successMessage && (
                    <p className="mt-4 text-sm text-green-400 font-medium text-start">
                      {successMessage}
                    </p>
                  )}
                </div>
              </div>
            </form>
          </motion.div>

          {/* contact info */}
          <motion.div
            variants={scrollVariantsRight}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.5 }}
            className="bg-slate-900/40 rounded-xl py-6 px-6 overflow-hidden relative"
          >
            <Grid size={20} />
            <div className="flex items-center gap-3">
              <div className="w-1 h-6 bg-gradient-to-b from-secondary to-gold rounded-sm" />
              <h3 className="text-lg leading-none font-semibold uppercase text-center">
                contact information
              </h3>
            </div>
            <div className="my-6 h-px bg-gradient-to-r from-gray-400 via-gray to-gray-400 animate-pulse" />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 gap-y-10 pt-8 p-4 px-0">
              {/* <div className="flex justify-start gap-2 items-start">
                <MapPin className="text-gold" size={22} />
                <div>
                  <h5 className="uppercase leading-none mb-2 font-semibold text-xl">
                    USA
                  </h5>
                  <p className="text-sm text-slate-400">
                    123 Business Avenue, <br />
                    Tech City, CA 90210
                  </p>
                </div>
              </div> */}

              <div className="flex justify-start gap-2 items-start">
                <MapPin className="text-gold" size={22} />
                <div>
                  <h5 className="uppercase leading-none mb-2 font-semibold text-xl">
                    UAE
                  </h5>
                  <p className="text-sm text-slate-400">
                    The Meydan Hotel, Grandstand, <br />
                    6th floor, Meydan Road, Nad Al Sheba, <br />
                    Dubai, UAE
                  </p>
                </div>
              </div>

              {/* <div className="flex justify-start gap-2 items-start">
                <MapPin className="text-gold" size={22} />
                <div>
                  <h5 className="uppercase leading-none mb-2 font-semibold text-xl">
                    UAE
                  </h5>
                  <p className="text-sm text-slate-400">
                    Office 805, Donna Tower, <br />
                    Dubai Silicon Oasis,
                    <br />
                    Dubai, UAE
                  </p>
                </div>
              </div> */}

              {/* <div className="flex justify-start gap-2 items-start">
                <PhoneForwarded className="text-gold" size={22} />
                <div>
                  <p className="text-lg leading-none mb-2 font-semibold">
                    Phone number (USA)
                  </p>
                  <p className="mt-1 text-sm font-medium text-slate-400">
                    +1 (555) 123-4567
                  </p>
                </div>
              </div> */}

              {/* <div className="flex justify-start gap-2 items-start">
                <MapPin className="text-gold" size={22} />
                <div>
                  <h5 className="uppercase mb-2 leading-none font-semibold text-xl">
                    CANADA
                  </h5>
                  <p className="text-sm text-slate-400 ">
                    101-310 Main St East, <br />
                    Milton, ON L9T 1P5
                  </p>
                </div>
              </div>

              <div className="flex justify-start gap-2 items-start">
                <PhoneForwarded className="text-gold" size={22} />
                <div>
                  <p className="text-lg leading-none font-semibold mb-1">
                    Phone number (CA)
                  </p>
                  <div className="flex flex-col">
                    <p className="mt-1 text-sm text-slate-400 font-medium">
                      +971 58 5696197
                    </p>
                    <p className="mt-1 text-sm font-medium text-slate-400">
                      +1 905 3796197
                    </p>
                  </div>
                </div>
              </div> */}

              {/* <div className="flex justify-start gap-2 items-start">
                <MapPin className="text-gold" size={22} />
                <div>
                  <h5 className="uppercase mb-2 leading-none font-semibold text-xl">
                    United Kingdom
                  </h5>
                  <p className="text-sm text-slate-400 ">
                    2nd floor, Berkeley Square House,
                    <br />
                    Berkeley Square, London W1J 6BD
                  </p>
                </div>
              </div> */}

              <div className="flex justify-start gap-2 items-start">
                <PhoneForwarded className="text-gold" size={22} />
                <div>
                  <p className="text-lg leading-none mb-2 font-semibold">
                    Phone number (UAE)
                  </p>
                  <p className="mt-1 text-sm font-medium text-slate-400">
                    +971 52 794 7540
                  </p>
                  <p className="mt-1 text-sm font-medium text-slate-400">
                    +971 4 557 9914
                  </p>
                </div>
              </div>

              <div className="flex justify-start gap-2 items-start">
                <Mail size={20} className="mb-1 text-gold" />
                <div>
                  <h5 className="mb-1 leading-none font-semibold text-xl">
                    Email address
                  </h5>
                  <p className="text-sm text-slate-400">admin@scanglobal.me</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* address */}
      <section className="pb-10 md:pb-16 lg:pb-24 pt-10">
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible"
            variants={downAndUp}
            viewport={{ amount: 0.3 }}
            className="max-w-xl mx-auto mb-12 text-center"
          >
            <h3 className="text-gold font-semibold text-sm uppercase tracking-wide mb-1">
              GLOBAL PRESENCE
            </h3>
            <h2 className="text-3xl font-bold sm:text-4xl mb-4">
              Our Global Offices
            </h2>
            <p className="text-gray-400 text-lg">
              With locations around the world, we provide localized support to
              clients in all major markets.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            variants={downAndUp}
            viewport={{ amount: 0.3 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8 mt-12 max-w-3xl mx-auto"
          >
            <div className="bg-slate-800/50 rounded-lg shadow-lg overflow-hidden transform transition-all duration-300 hover:-translate-y-2 hover:shadow-xl">
              <div
                className="h-48 bg-cover bg-center"
                style={{
                  backgroundImage: `url("/images/solutions/4.jpg")`,
                }}
              ></div>
              <div className="p-6">
                <h3 className="text-xl font-bold mb-2">Dubai, UAE</h3>
                <div className="flex items-start mb-4">
                  <MapPin className="h-5 w-5 text-gold mr-2 mt-0.5" />
                  <p className="text-gray-400 leading-tight">
                    The Meydan Hotel, Grandstand, 6th floor, Meydan Road, Nad Al
                    Sheba
                  </p>
                </div>
                <Link
                  target="_blank"
                  to="https://www.google.com/maps/place/The+Meydan+Hotel/@25.1556628,55.2977263,17z/data=!3m1!4b1!4m9!3m8!1s0x3e5f688c5516ea0f:0x44800f32689f57e2!5m2!4m1!1i2!8m2!3d25.155658!4d55.3003012!16s%2Fg%2F11c2yrjfv6?entry=ttu&g_ep=EgoyMDI0MDkwMi4xIKXMDSoASAFQAw%3D%3D"
                  className="underline text-gold flex gap-1 items-center leading-none"
                >
                  Direction <ArrowUpRight size={14} className="leading-none" />
                </Link>
              </div>
            </div>

            <div className="bg-slate-800/50 rounded-lg shadow-lg overflow-hidden transform transition-all duration-300 hover:-translate-y-2 hover:shadow-xl">
              <div
                className="h-48 bg-cover bg-center"
                style={{
                  backgroundImage: `url("/images/solutions/5.jpg")`,
                }}
              ></div>
              <div className="p-6">
                <h3 className="text-xl font-bold mb-2">Dubai, UAE</h3>
                <div className="flex items-start mb-4">
                  <MapPin className="h-5 w-5 text-gold mr-2 mt-0.5" />
                  <p className="text-gray-400 leading-tight">
                    Office 805, Donna Towers, Dubai Silicon Oasis, Dubai, UAE
                  </p>
                </div>
                <Link
                  target="_blank"
                  to="https://www.google.com/maps/search/Office+805,+Donna+Tower,+Dubai+Silicon+Oasis/@25.1172059,55.3669949,17z/data=!3m1!4b1?entry=ttu&g_ep=EgoyMDI0MDkwMi4xIKXMDSoASAFQAw%3D%3D"
                  className="underline text-gold flex gap-1 items-center leading-none"
                >
                  Direction <ArrowUpRight size={14} className="leading-none" />
                </Link>
              </div>
            </div>

            {/* <div className="bg-slate-800/50 rounded-lg shadow-lg overflow-hidden transform transition-all duration-300 hover:-translate-y-2 hover:shadow-xl">
              <div
                className="h-48 bg-cover bg-center"
                style={{
                  backgroundImage: `url("/images/solutions/5.jpg")`,
                }}
              ></div>
              <div className="p-6">
                <h3 className="text-xl font-bold mb-2">Toronto Office</h3>
                <div className="flex items-start mb-4">
                  <MapPin className="h-5 w-5 text-gold mr-2 mt-0.5" />
                  <p className="text-gray-400 leading-tight">
                    200 Bay Street, Office 3000
                    <br />
                    Toronto, ON M5J 2J1
                    <br />
                    Canada
                  </p>
                </div>
                <div className="flex items-center mb-2">
                  <Phone className="h-5 w-5 text-gold mr-2" />
                  <p className="text-gray-400 leading-tight">+1 416 123 4567</p>
                </div>
                <div className="flex items-center">
                  <Mail className="h-5 w-5 text-gold mr-2" />
                  <p className="text-gray-400 leading-tight">toronto@scanglobal.com</p>
                </div>
              </div>
            </div> */}

            {/* <div className="bg-slate-800/50 rounded-lg shadow-lg overflow-hidden transform transition-all duration-300 hover:-translate-y-2 hover:shadow-xl">
              <div
                className="h-48 bg-cover bg-center"
                style={{
                  backgroundImage: `url(https://images.pexels.com/photos/1519088/pexels-photo-1519088.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2)`,
                }}
              ></div>
              <div className="p-6">
                <h3 className="text-xl font-bold mb-2">London Office</h3>
                <div className="flex items-start mb-4">
                  <MapPin className="h-5 w-5 text-gold mr-2 mt-0.5" />
                  <p className="text-gray-400 leading-tight">
                    30 St Mary Axe
                    <br />
                    London EC3A 8BF
                    <br />
                    United Kingdom
                  </p>
                </div>
                <div className="flex items-center mb-2">
                  <Phone className="h-5 w-5 text-gold mr-2" />
                  <p className="text-gray-400 leading-tight">+44 20 1234 5678</p>
                </div>
                <div className="flex items-center">
                  <Mail className="h-5 w-5 text-gold mr-2" />
                  <p className="text-gray-400 leading-tight">london@scanglobal.com</p>
                </div>
              </div>
            </div> */}
          </motion.div>
        </div>
      </section>

      {/* FAQs Section */}
      <section className="pb-10 md:pb-16 lg:pb-24">
        <div className="container mx-auto px-4 max-w-3xl">
          <motion.div
            initial="hidden"
            whileInView="visible"
            variants={downAndUp}
            viewport={{ amount: 0.3 }}
            className="max-w-3xl mx-auto mb-12 text-center"
          >
            <h3 className="text-gold font-semibold text-sm uppercase tracking-wide mb-1">
              FAQS
            </h3>
            <h2 className="text-3xl font-bold sm:text-4xl mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-gray-400 text-lg">
              Find answers to common questions about our services and solutions.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            variants={downAndUp}
            viewport={{ amount: 0.3 }}
            className="max-w-4xl mx-auto mt-12 space-y-6"
          >
            <div className="bg-slate-800/50 rounded-lg shadow-md px-6 py-3 transform transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <HelpCircle className="h-6 w-6 text-gold" />
                </div>
                <div className="ml-4">
                  <h4 className="text-lg font-semibold mb-2 leading-none">
                    What services does Scan Global Solutions offer?
                  </h4>
                  <p className="text-gray-400 leading-tight">
                    We provide comprehensive business services including real
                    estate, auditing, business advisory, global business
                    immigration, business brokerage, and global HR solutions.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-slate-800/50 rounded-lg shadow-md px-6 py-3 transform transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <HelpCircle className="h-6 w-6 text-gold" />
                </div>
                <div className="ml-4">
                  <h4 className="text-lg font-semibold mb-2 leading-none">
                    What is the process for your business advisory services
                  </h4>
                  <p className="text-gray-400 leading-tight">
                    Our business advisory services include an initial meeting to
                    understand your objectives, information collection and
                    assessment, reporting and delivery, and providing tailored
                    solutions and results.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-slate-800/50 rounded-lg shadow-md px-6 py-3 transform transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <HelpCircle className="h-6 w-6 text-gold" />
                </div>
                <div className="ml-4">
                  <h4 className="text-lg font-semibold mb-2 leading-none">
                    Can Scan Global Solutions help with international business
                    immigration?
                  </h4>
                  <p className="text-gray-400 leading-tight">
                    Yes, we offer specialized services to navigate the
                    complexities of global business immigration, including
                    assistance with processes for Canada, the USA, and Europe.
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-slate-800/50 rounded-lg shadow-md px-6 py-3 transform transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <HelpCircle className="h-6 w-6 text-gold" />
                </div>
                <div className="ml-4">
                  <h4 className="text-lg font-semibold mb-2 leading-none">
                    What makes Scan Global Solutions different from other
                    consultancy firms?
                  </h4>
                  <p className="text-gray-400 leading-tight">
                    Our tailored and comprehensive solutions, experienced
                    professionals, and commitment to excellence set us apart in
                    the industry.
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-slate-800/50 rounded-lg shadow-md px-6 py-3 transform transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <HelpCircle className="h-6 w-6 text-gold" />
                </div>
                <div className="ml-4">
                  <h4 className="text-lg font-semibold mb-2 leading-none">
                    How do I book a consultation?
                  </h4>
                  <p className="text-gray-400 leading-tight">
                    Consultations can be booked directly through our website by
                    and filling out the necessary details.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* careers */}
      <section className="pb-10 md:pb-16 lg:pb-24">
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible"
            variants={downAndUp}
            viewport={{ amount: 0.3 }}
            className="max-w-3xl mx-auto mb-12 text-center"
          >
            <h3 className="text-gold font-semibold text-sm uppercase tracking-wide mb-1">
              CAREERS
            </h3>
            <h2 className="text-3xl font-bold sm:text-4xl mb-4">
              Join Our Team
            </h2>
            <p className="text-gray-400 text-lg">
              Explore exciting career opportunities at Scan Global Solutions.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            variants={downAndUp}
            viewport={{ amount: 0.3 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-12"
          >
            <div className="bg-slate-800/50 rounded-lg shadow-lg px-6 py-3 transform transition-all duration-300 hover:-translate-y-2 hover:shadow-xl">
              <div className="w-12 h-12 bg-gold/10 rounded-lg flex items-center justify-center mb-6">
                <Briefcase className="h-6 w-6 text-gold" />
              </div>
              <h3 className="text-xl font-bold mb-2">
                Senior Software Engineer
              </h3>
              <p className="text-gray-400 mb-4">Dubai, UAE</p>
              <ul className="text-gray-400 mb-6 space-y-2">
                <li>• 5+ years of experience</li>
                <li>• Full-stack development</li>
                <li>• Cloud technologies</li>
              </ul>
              <button
                className="bg-white text-slate-900 hover:bg-gold hover:text-slate-900 py-1.5 px-4 rounded"
                onClick={scrollToContact}
              >
                Apply Now
              </button>
            </div>

            <div className="bg-slate-800/50 rounded-lg shadow-lg px-6 py-3 transform transition-all duration-300 hover:-translate-y-2 hover:shadow-xl">
              <div className="w-12 h-12 bg-gold/10 rounded-lg flex items-center justify-center mb-6">
                <Briefcase className="h-6 w-6 text-gold" />
              </div>
              <h3 className="text-xl font-bold mb-2">Solutions Architect</h3>
              <p className="text-gray-400 mb-4">Toronto, Canada</p>
              <ul className="text-gray-400 mb-6 space-y-2">
                <li>• 8+ years of experience</li>
                <li>• Enterprise architecture</li>
                <li>• Technical leadership</li>
              </ul>
              <button
                className="bg-white text-slate-900 hover:bg-gold hover:text-slate-900 py-1.5 px-4 rounded"
                onClick={scrollToContact}
              >
                Apply Now
              </button>
            </div>

            <div className="bg-slate-800/50 rounded-lg shadow-lg px-6 py-3 transform transition-all duration-300 hover:-translate-y-2 hover:shadow-xl">
              <div className="w-12 h-12 bg-gold/10 rounded-lg flex items-center justify-center mb-6">
                <Briefcase className="h-6 w-6 text-gold" />
              </div>
              <h3 className="text-xl font-bold mb-2">Project Manager</h3>
              <p className="text-gray-400 mb-4">London, UK</p>
              <ul className="text-gray-400 mb-6 space-y-2">
                <li>• 6+ years of experience</li>
                <li>• Agile methodologies</li>
                <li>• Client management</li>
              </ul>
              <button
                className="bg-white text-slate-900 hover:bg-gold hover:text-slate-900 py-1.5 px-4 rounded"
                onClick={scrollToContact}
              >
                Apply Now
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/*  */}
    </>
  );
};

export default Contact;
